import { useEffect, useMemo, useRef, useState } from 'react';
import { api, networkWords } from '../api.js';
import { useConfig } from '../config.js';
import {
  ConnectedDevices,
  DeviceDetail,
  HydrosDetail,
  ConnectDevice,
  useTankDevices,
  useHydrosLink,
} from './DevicesHome.jsx';
import { useConfirm } from './ConfirmDialog.jsx';
import { RingCameraDetail, useTankRing } from './RingCamera.jsx';
import {
  getSections,
  getBrandsForKind,
  getSubtypesForKind,
  kindMeta,
  unTrackedSuggestions,
  catalogPartByName,
  newPartBody,
  calendarDayAgo,
  partStatus,
  serviceStatus,
  rollupStatus,
  retryChanges,
} from '../equipment-catalog.js';
import { EquipmentIcon } from './EquipmentIcon.jsx';
import { resolveReorderUrl } from '../reorder.js';
import {
  normalizeReorderLink,
  PART_HOMEPAGE_HINT,
  PART_LINK_ERROR,
} from '../../../shared/reorder-link.js';
import { track } from '../events.js';
import { localDateString } from '../fill-date.js';
import { claimRequest, settleRequest, unsureSaveWords } from '../client-ref.js';

// Per-tank equipment registry. The picker is built around taps, not
// typing: a section grid → optional sub-type → brand chips → optional
// parts-to-track step. Free-text fallback at every step so anything
// not in the seed catalog still has a home. Each equipment row owns
// child PARTS (UV bulbs, probes, impellers, RO/DI cartridges) with
// their own replace cadence — that's how the "tell me to reorder
// before I run out" promise gets to be true.

// The keeper's own calendar day: toISOString is UTC, tomorrow by evening in
// the Americas.
const TODAY = () => localDateString();

export function EquipmentSection({ tank, onReload, user, initialDevice = null }) {
  const confirm = useConfirm();
  const config = useConfig();
  const catalog = config.equipment;
  const items = tank.equipment || [];
  const [pickerOpen, setPickerOpen] = useState(false);
  const [adding, setAdding] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  // A viewer's add / edit / remove would only answer 403.
  const canEdit = tank.role !== 'viewer';
  // 'paired' | 'elsewhere' | 'none' | 'unknown' | null. 'unknown' (the
  // device list failed to load) claims nothing about what the keeper owns.
  // TankSensei cannot reach an Apex by itself:
  // it is read on the keeper's own network by a Printed Reef display or
  // hub, so picking Apex explains that instead of opening a form.
  const [apexNote, setApexNote] = useState(null);
  // Connected devices first, each with its own page (DevicesHome.jsx):
  // null | { kind: 'device', id } | { kind: 'hydros' } | { kind: 'connect' }.
  // Home opens a device's own page: the quiet-device row ('device:<id>') and
  // the HYDROS tile's "Pick probes" ('hydros').
  const [devices, reloadDevices, devicesFailed] = useTankDevices(tank);
  const hydrosLink = useHydrosLink(tank);
  const [ring, reloadRing] = useTankRing(tank);
  const [deviceScreen, setDeviceScreen] = useState(() =>
    initialDevice === 'hydros'
      ? { kind: 'hydros' }
      : typeof initialDevice === 'string' && initialDevice.startsWith('device:')
        ? { kind: 'device', id: initialDevice.slice('device:'.length) }
        : null
  );
  const isOwner = !tank.role || tank.role === 'owner';
  // Parts saved here that the tank's reload has not brought back yet (a weak
  // signal). Kept per equipment row, not in the card: a card collapsed and
  // opened again lost them, offered the chip again, and a tap saved a twin
  // (review, 2026-10-24). The header counts them too.
  const [justAdded, setJustAdded] = useState({});
  // A part's answer from Replaced / Serviced today or Remove, shown until
  // the tank's next reload carries it (the reload can fail after the save).
  const [fresh, setFresh] = useState({});
  const partsOf = (r) => {
    const saved = r.parts || [];
    const extra = (justAdded[r.id] || []).filter((a) => !saved.some((p) => p.id === a.id));
    const all = extra.length ? [...saved, ...extra] : saved;
    if (!Object.keys(fresh).length) return all;
    return all.filter((p) => !fresh[p.id]?.removed).map((p) => fresh[p.id] || p);
  };
  // Once a reload carries a part it is the reload's, so a part removed on
  // another phone does not come back from here.
  useEffect(() => {
    setJustAdded((prev) => {
      let changed = false;
      const next = {};
      for (const [eqId, rows] of Object.entries(prev)) {
        const saved = (tank.equipment || []).find((e) => e.id === eqId)?.parts || [];
        const keep = rows.filter((a) => !saved.some((p) => p.id === a.id));
        if (keep.length !== rows.length) changed = true;
        if (keep.length) next[eqId] = keep;
      }
      return changed ? next : prev;
    });
    setFresh((prev) => (Object.keys(prev).length ? {} : prev));
  }, [tank.equipment]);
  const forgetPart = (eqId, partId) =>
    setJustAdded((prev) =>
      prev[eqId] ? { ...prev, [eqId]: prev[eqId].filter((a) => a.id !== partId) } : prev
    );

  // Sort: status-first (needs attention bubbles up), then by kind.
  const sorted = useMemo(() => {
    const STATUS_RANK = { critical: 0, low: 1, ok: 2, untracked: 3, none: 4 };
    return [...items].sort((a, b) => {
      const sa = STATUS_RANK[rollupStatus(partsOf(a)).kind];
      const sb = STATUS_RANK[rollupStatus(partsOf(b)).kind];
      if (sa !== sb) return sa - sb;
      return (a.kind || '').localeCompare(b.kind || '');
    });
  }, [items, justAdded, fresh]);

  const toggleExpand = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const removeRow = async (row) => {
    const meta = kindMeta(catalog, row.kind);
    const ok = await confirm({
      title: `Remove ${meta.label}${row.brand ? ` (${row.brand})` : ''}?`,
      body: 'All parts tracked under this equipment will also be removed. Linked maintenance tasks stay on the tank but stop auto-renewing.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setBusy(`del:${row.id}`);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/equipment/${row.id}`, { method: 'DELETE' });
      await onReload();
    } catch (e) {
      setErr(e.message || 'Could not delete.');
    } finally {
      setBusy(null);
    }
  };

  // Screen swap: adding equipment isn't a popup — it's a full screen
  // that replaces the section content (back button → list). The
  // category picker is one screen, the configure form is the next.
  // Cancel walks back through the stack one step at a time:
  //   add form → picker → equipment list.
  if (adding) {
    return (
      <section className="ts-section eq-screen">
        <div className="eq-screen-head">
          <button
            type="button"
            className="eq-back"
            onClick={() => setAdding(null)}
            aria-label="Back to category picker"
          >
            ← Back
          </button>
          <h3 className="font-heading" style={{ margin: 0 }}>
            Add {kindMeta(catalog, adding.kind).label.toLowerCase()}
          </h3>
        </div>
        {err && (
          <div role="alert" className="ts-err">
            {err}
          </div>
        )}
        <AddEquipmentSheet
          tank={tank}
          catalog={catalog}
          state={adding}
          onChange={setAdding}
          onDone={async () => {
            await onReload();
            setAdding(null);
            setPickerOpen(false);
          }}
          setErr={setErr}
        />
      </section>
    );
  }
  const backToList = async () => {
    setDeviceScreen(null);
    await reloadDevices();
  };
  if (deviceScreen?.kind === 'device') {
    const device = (devices || []).find((d) => d.id === deviceScreen.id);
    if (device) {
      return (
        <DeviceDetail
          tank={tank}
          device={device}
          deviceCount={(devices || []).length}
          onBack={backToList}
          onRemoved={async () => {
            await backToList();
            await onReload?.();
          }}
        />
      );
    }
  }
  if (deviceScreen?.kind === 'ring' && ring?.available) {
    return <RingCameraDetail tank={tank} ring={ring} onReload={reloadRing} onBack={backToList} />;
  }
  if (deviceScreen?.kind === 'hydros') {
    return <HydrosDetail tank={tank} onReload={onReload} onBack={backToList} />;
  }
  if (deviceScreen?.kind === 'connect') {
    return (
      <ConnectDevice
        tank={tank}
        onReload={async () => {
          await reloadDevices();
          await onReload?.();
        }}
        onBack={backToList}
      />
    );
  }

  if (pickerOpen) {
    return (
      <section className="ts-section eq-screen">
        <div className="eq-screen-head">
          <button
            type="button"
            className="eq-back"
            onClick={() => setPickerOpen(false)}
            aria-label="Back to equipment list"
          >
            ← Back
          </button>
          <h3 className="font-heading" style={{ margin: 0 }}>
            Pick a category
          </h3>
        </div>
        {err && (
          <div role="alert" className="ts-err">
            {err}
          </div>
        )}
        <KindPicker
          catalog={catalog}
          onPick={(kind) => {
            // Live-integration kinds bounce to the relevant panel in
            // the equipment list (HydrosLinkPanel / DevicesPanel)
            // rather than opening the add form — the data model for
            // those lives in HYDROS / Devices already.
            const integration = kindMeta(catalog, kind).integration;
            if (integration === 'apex_via_view7') {
              setErr('');
              setPickerOpen(false);
              api('/api/devices/mine')
                .then((r) => {
                  const mine = r?.devices || [];
                  setApexNote(
                    mine.some((d) => d.tank_id === tank.id)
                      ? 'paired'
                      : mine.length
                        ? 'elsewhere'
                        : 'none'
                  );
                })
                .catch(() => setApexNote('unknown'));
              return;
            }
            if (integration) {
              // A connected device is linked, not typed in: the owner goes
              // straight to Connect a device; anyone else is told whose it is.
              setPickerOpen(false);
              if (isOwner) {
                setErr('');
                setDeviceScreen({ kind: 'connect' });
              } else {
                setErr("A connected device is linked by the tank's owner.");
              }
              return;
            }
            setAdding({
              kind,
              subtype: '',
              brand: '',
              brandPicked: false,
              model: '',
              installed_at: TODAY(),
              notes: '',
              parts: [],
              partsLoaded: false,
            });
          }}
        />
      </section>
    );
  }

  return (
    <section className="ts-section">
      <div className="ts-section-head">
        <h3 className="font-heading">Equipment and devices</h3>
        <p className="ts-hint">
          Connected devices first, then your gear: skimmer, pumps, lights, RO/DI, anything with
          parts to replace. We track each part's replace cadence so you know what to reorder before
          it dies.
        </p>
      </div>

      <ConnectedDevices
        tank={tank}
        devices={devices}
        error={devicesFailed && devices === null}
        onRetry={reloadDevices}
        hydros={hydrosLink}
        ring={ring}
        onOpen={setDeviceScreen}
      />

      {apexNote && (
        <div className="eq-apex-note" role="status">
          <strong className="font-heading">Neptune Apex needs a Printed Reef display or hub</strong>
          {apexNote === 'paired' ? (
            <p>
              Your display reads the Apex on your home network and sends its probes here. Make sure
              it is listed under Connected above, and set your Apex's local username and password on
              the display.
            </p>
          ) : apexNote === 'elsewhere' ? (
            <p>
              Your Printed Reef display is linked to another tank. Readings from an Apex land on the
              tank its display is linked to. You can move it to this tank from your Account.
            </p>
          ) : apexNote === 'unknown' ? (
            <p>
              TankSensei can't connect to an Apex on its own: a Printed Reef display or hub on the
              same network as your Apex reads it and sends its probes here. If you have one, link it
              to this tank from Connect a device above.
            </p>
          ) : (
            <p>
              TankSensei can't connect to an Apex on its own. A Printed Reef display or hub sits on
              the same network as your Apex, reads its probes (including Trident results) and sends
              them here. The hub collects data from the reef controllers on your home network the
              same way. Both are coming soon.
            </p>
          )}
          <div className="eq-apex-actions">
            {apexNote === 'none' && (
              <a href="/display" target="_blank" rel="noreferrer" className="font-heading">
                See the displays
              </a>
            )}
            <button type="button" onClick={() => setApexNote(null)}>
              Got it
            </button>
          </div>
        </div>
      )}

      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}

      {((devices || []).length > 0 || tank.hydros_thing_id) && (
        <div className="dvh-group-label font-heading" style={{ marginTop: 16 }}>
          Gear
        </div>
      )}
      {sorted.length === 0 ? (
        <p className="ts-hint" style={{ marginTop: 14 }}>
          {canEdit
            ? 'Nothing logged yet. Add your skimmer, RO/DI, lighting, or anything else — pick a category below.'
            : 'No equipment logged on this tank yet.'}
        </p>
      ) : (
        <ul className="eq-list">
          {sorted.map((r) => {
            const meta = kindMeta(catalog, r.kind);
            const tail = [r.brand, r.model].filter(Boolean).join(' ').trim();
            const rParts = partsOf(r);
            const status = rollupStatus(rParts);
            const open = expanded.has(r.id);
            return (
              <li key={r.id} className="eq-item">
                <button
                  type="button"
                  className="eq-item-row"
                  onClick={() => toggleExpand(r.id)}
                  aria-expanded={open}
                >
                  <span className="eq-emoji" aria-hidden="true">
                    <EquipmentIcon kind={r.kind} size={22} fallback={meta.icon} />
                  </span>
                  <div className="eq-item-text">
                    <div className="eq-item-name">
                      {meta.label}
                      {tail && <span className="eq-item-tail"> · {tail}</span>}
                    </div>
                    <div className="eq-item-meta">
                      {rParts.length
                        ? `${rParts.length} part${rParts.length === 1 ? '' : 's'} tracked`
                        : 'No parts tracked'}
                      {r.installed_at && ` · installed ${String(r.installed_at).slice(0, 10)}`}
                    </div>
                  </div>
                  <span className={`eq-status eq-status-${status.kind}`}>{status.label}</span>
                  <span
                    className={`eq-chevron${open ? ' eq-chevron-open' : ''}`}
                    aria-hidden="true"
                  >
                    ▾
                  </span>
                </button>

                {open && (
                  <div className="eq-expand">
                    <PartsList
                      tank={tank}
                      equipment={r}
                      parts={rParts}
                      onAdded={(row) =>
                        setJustAdded((prev) => ({ ...prev, [r.id]: [...(prev[r.id] || []), row] }))
                      }
                      onForget={(partId) => forgetPart(r.id, partId)}
                      onFresh={(row) => setFresh((prev) => ({ ...prev, [row.id]: row }))}
                      onReload={onReload}
                      setErr={setErr}
                      busy={busy}
                      setBusy={setBusy}
                      user={user}
                      canEdit={canEdit}
                    />
                    {canEdit && (
                      <div className="eq-expand-actions">
                        <button
                          type="button"
                          className="ts-chip"
                          onClick={() =>
                            setEditing({
                              id: r.id,
                              kind: r.kind,
                              brand: r.brand || '',
                              model: r.model || '',
                              installed_at: r.installed_at
                                ? String(r.installed_at).slice(0, 10)
                                : '',
                              notes: r.notes || '',
                            })
                          }
                        >
                          Edit details
                        </button>
                        <button
                          type="button"
                          className="ts-chip ts-chip-danger"
                          onClick={() => removeRow(r)}
                          disabled={busy === `del:${r.id}`}
                        >
                          {busy === `del:${r.id}` ? '…' : 'Remove'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canEdit && (
        <button
          type="button"
          className="ts-add-btn"
          onClick={() => {
            track('equipment.add_open', 'settings');
            setPickerOpen(true);
          }}
        >
          + Add equipment
        </button>
      )}

      {editing && (
        <EditEquipmentDialog
          tank={tank}
          catalog={catalog}
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await onReload();
            setEditing(null);
          }}
          setErr={setErr}
        />
      )}
    </section>
  );
}

// ── Category grid ───────────────────────────────────────────────
function KindPicker({ catalog, onPick }) {
  const sections = getSections(catalog);
  return (
    <div className="eq-picker">
      {sections.map((section) => (
        <div key={section.title} className="eq-picker-section">
          <div className="eq-picker-title">{section.title}</div>
          <div className="eq-picker-grid">
            {section.kinds.map((k) => {
              const meta = kindMeta(catalog, k);
              return (
                <button
                  type="button"
                  key={k}
                  className="eq-tile"
                  onClick={() => onPick(k)}
                  aria-label={meta.label}
                >
                  <span className="eq-tile-icon" aria-hidden="true">
                    <EquipmentIcon kind={k} size={32} fallback={meta.icon} />
                  </span>
                  <span className="eq-tile-label">{meta.label}</span>
                  {meta.integration === 'apex_via_view7' ? (
                    <span className="eq-tile-pill">Display or hub</span>
                  ) : (
                    meta.integration && <span className="eq-tile-pill">Live data</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Add flow (sub-type → brand → parts) ─────────────────────────
function AddEquipmentSheet({ tank, catalog, state, onChange, onDone, setErr }) {
  const meta = kindMeta(catalog, state.kind);
  const subtypesArr = getSubtypesForKind(catalog, state.kind);
  const subtypes = subtypesArr.length ? subtypesArr : null;
  const brands = getBrandsForKind(catalog, state.kind);
  const [busy, setBusy] = useState(false);
  const [showOtherBrand, setShowOtherBrand] = useState(false);

  // Lazy-load suggested parts the first time the sheet renders, so
  // a kind with no template doesn't pay any network. Result is
  // stored on the state so the user can untick/edit before save.
  if (!state.partsLoaded) {
    api(`/api/tanks/${tank.id}/equipment/parts/suggested?kind=${encodeURIComponent(state.kind)}`)
      .then((rows) =>
        onChange((s) => ({
          ...s,
          partsLoaded: true,
          parts: (Array.isArray(rows) ? rows : []).map((p) => ({
            ...p,
            keep: true,
            installed_at: s.installed_at,
          })),
        }))
      )
      .catch(() => onChange((s) => ({ ...s, partsLoaded: true })));
  }

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      const eq = await api(`/api/tanks/${tank.id}/equipment`, {
        method: 'POST',
        body: {
          kind: state.kind,
          brand: state.brand?.trim() || null,
          model:
            [state.subtype, state.model].filter(Boolean).join(' ').trim().slice(0, 120) || null,
          installed_at: state.installed_at || null,
          replace_every_days: null,
          notes: state.notes?.trim() || null,
        },
      });
      // Seed each ticked part. Sequential intentionally — the
      // parent already exists, the order of inserts doesn't matter
      // for the user, and one failure shouldn't block the rest.
      for (const p of state.parts || []) {
        if (!p.keep) continue;
        try {
          await api(`/api/tanks/${tank.id}/equipment/${eq.id}/parts`, {
            method: 'POST',
            body: {
              kind: p.kind,
              label: p.label,
              brand: state.brand?.trim() || null,
              installed_at: p.installed_at || state.installed_at || null,
              replace_every_days: p.replace_every_days || null,
              reorder_url: p.reorder_url || null,
              // Phase 5 — carry the maintenance cadence from the
              // catalog template so "clean fans every 90d" lands with
              // the part instead of being lost on add.
              service_every_days: p.service_every_days || null,
              service_label: p.service_label || null,
            },
          });
        } catch (e) {
          setErr(`Saved ${meta.label} but failed to add ${p.label}: ${e.message}`);
        }
      }
      track('equipment.add_done', 'settings', { kind: state.kind });
      await onDone();
    } catch (e) {
      setErr(e.message || 'Could not add equipment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ts-add-form eq-add-form">
      {/* Title icon: large, centred, gives the form an at-a-glance
          identity. Back navigation lives in the screen header above
          this component — no Cancel chip here. */}
      <div className="eq-add-hero" aria-hidden="true">
        <EquipmentIcon kind={state.kind} size={44} fallback={meta.icon} />
      </div>

      {subtypes && (
        <div className="eq-step">
          <div className="eq-step-label">Type</div>
          <div className="eq-chip-row">
            {subtypes.map((t) => (
              <button
                type="button"
                key={t}
                className={`eq-chip${state.subtype === t ? ' eq-chip-on' : ''}`}
                onClick={() => onChange({ ...state, subtype: t })}
              >
                {t}
              </button>
            ))}
            <button
              type="button"
              className={`eq-chip${state.subtype && !subtypes.includes(state.subtype) ? ' eq-chip-on' : ''}`}
              onClick={() => {
                const custom = prompt('Other type:');
                if (custom) onChange({ ...state, subtype: custom.slice(0, 60) });
              }}
            >
              Other…
            </button>
          </div>
        </div>
      )}

      {brands.length > 0 && (
        <div className="eq-step">
          <div className="eq-step-label">Brand</div>
          <div className="eq-chip-row">
            {brands.map((b) => (
              <button
                type="button"
                key={b}
                className={`eq-chip${state.brand === b ? ' eq-chip-on' : ''}`}
                onClick={() => {
                  setShowOtherBrand(false);
                  onChange({ ...state, brand: b, brandPicked: true });
                }}
              >
                {b}
              </button>
            ))}
            <button
              type="button"
              className={`eq-chip${showOtherBrand ? ' eq-chip-on' : ''}`}
              onClick={() => {
                setShowOtherBrand(true);
                onChange({ ...state, brand: '', brandPicked: true });
              }}
            >
              Other…
            </button>
          </div>
          {showOtherBrand && (
            <input
              type="text"
              autoFocus
              value={state.brand}
              maxLength={80}
              placeholder="Brand name"
              onChange={(e) => onChange({ ...state, brand: e.target.value })}
              className="eq-text-input"
            />
          )}
        </div>
      )}

      {brands.length === 0 && (
        <div className="eq-step">
          <div className="eq-step-label">Brand (optional)</div>
          <input
            type="text"
            value={state.brand}
            maxLength={80}
            placeholder="Brand name"
            onChange={(e) => onChange({ ...state, brand: e.target.value })}
            className="eq-text-input"
          />
        </div>
      )}

      <div className="eq-step eq-step-row">
        <label className="ts-field" style={{ minWidth: 140 }}>
          <span>Installed</span>
          <input
            type="date"
            value={state.installed_at}
            onChange={(e) =>
              onChange({
                ...state,
                installed_at: e.target.value,
                parts: (state.parts || []).map((p) => ({ ...p, installed_at: e.target.value })),
              })
            }
          />
        </label>
        <label className="ts-field" style={{ minWidth: 160, flex: '1 1 160px' }}>
          <span>Model (optional)</span>
          <input
            type="text"
            value={state.model}
            maxLength={120}
            placeholder="Classic 200"
            onChange={(e) => onChange({ ...state, model: e.target.value })}
          />
        </label>
      </div>

      {state.partsLoaded && state.parts?.length > 0 && (
        <div className="eq-step">
          <div className="eq-step-label">We'll also track these parts (untick any you skip)</div>
          {state.parts.map((p, idx) => (
            <div key={`${p.kind}-${idx}`} className="eq-part-row">
              <label className="eq-part-keep">
                <input
                  type="checkbox"
                  checked={p.keep}
                  onChange={(e) =>
                    onChange({
                      ...state,
                      parts: state.parts.map((x, i) =>
                        i === idx ? { ...x, keep: e.target.checked } : x
                      ),
                    })
                  }
                />
                <span>{p.label}</span>
              </label>
              <div className="eq-part-cadence">
                <span className="ts-hint">every</span>
                <input
                  type="number"
                  min="1"
                  value={p.replace_every_days || ''}
                  onChange={(e) =>
                    onChange({
                      ...state,
                      parts: state.parts.map((x, i) =>
                        i === idx ? { ...x, replace_every_days: Number(e.target.value) || null } : x
                      ),
                    })
                  }
                  className="eq-part-days"
                />
                <span className="ts-hint">days</span>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="ts-chip"
            onClick={() =>
              onChange({
                ...state,
                parts: [
                  ...state.parts,
                  {
                    kind: 'custom',
                    label: '',
                    replace_every_days: 365,
                    keep: true,
                    installed_at: state.installed_at,
                    custom: true,
                  },
                ],
              })
            }
          >
            + Add custom part
          </button>
        </div>
      )}

      {state.partsLoaded &&
        state.parts?.some((p) => p.custom) &&
        state.parts.map(
          (p, idx) =>
            p.custom && (
              <div key={`custom-${idx}`} className="eq-part-row">
                <input
                  type="text"
                  value={p.label}
                  maxLength={120}
                  placeholder="Part name"
                  onChange={(e) =>
                    onChange({
                      ...state,
                      parts: state.parts.map((x, i) =>
                        i === idx ? { ...x, label: e.target.value } : x
                      ),
                    })
                  }
                  className="eq-text-input"
                />
              </div>
            )
        )}

      <div className="eq-submit-row">
        <button type="button" className="ts-save" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Inline parts list inside the expanded card ──────────────────
function PartsList({
  tank,
  equipment,
  parts = equipment.parts || [],
  onAdded,
  onForget,
  onFresh,
  onReload,
  setErr,
  busy,
  setBusy,
  user,
  canEdit = true,
}) {
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();
  const config = useConfig();
  const catalogParts = kindMeta(config?.equipment, equipment.kind).parts;

  // Said under the part's own row (a weak signal's "Load failed" sat above
  // the list, off screen), and a save is never reported failed because only
  // the reload after it failed: the row reads its new date from the answer
  // (review, 2026-10-24).
  const [rowMsg, setRowMsg] = useState(null);
  const partAction = async (partId, key, send, fallback) => {
    setBusy(`${key}:${partId}`);
    setErr('');
    setRowMsg(null);
    let row;
    try {
      row = await send();
    } catch (e) {
      setBusy(null);
      if (e.status === 404) {
        onForget?.(partId);
        setRowMsg({ id: 'gone', text: 'That part was removed on another phone.' });
        await onReload().catch(() => {});
      } else setRowMsg({ id: partId, text: networkWords(e) || e.message || fallback });
      return;
    }
    onFresh?.(row?.id ? row : { id: partId, removed: true });
    setBusy(null);
    await onReload().catch(() => {});
  };
  const onReplaced = (partId) =>
    partAction(
      partId,
      'replaced',
      () =>
        api(`/api/tanks/${tank.id}/equipment/parts/${partId}/replaced`, {
          method: 'POST',
          body: { day: TODAY() },
        }),
      'Could not mark it replaced.'
    );
  const onServiced = (partId) =>
    partAction(
      partId,
      'serviced',
      () =>
        api(`/api/tanks/${tank.id}/equipment/parts/${partId}/serviced`, {
          method: 'POST',
          body: { day: TODAY() },
        }),
      'Could not mark it serviced.'
    );
  const onRemove = async (partId) => {
    const part = parts.find((x) => x.id === partId);
    const ok = await confirm({
      title: `Remove ${part?.label || 'this part'}?`,
      body: 'TankSensei stops tracking it, and its dates and reorder link go with it.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    await partAction(
      partId,
      'del-part',
      async () => {
        await api(`/api/tanks/${tank.id}/equipment/parts/${partId}`, { method: 'DELETE' });
        onForget?.(partId);
        return null;
      },
      'Could not remove it.'
    );
  };

  return (
    <div className="eq-parts">
      <div className="eq-parts-head">
        <div className="eq-parts-title">Parts</div>
      </div>
      {rowMsg?.id === 'gone' && (
        <p role="alert" className="eq-field-error">
          {rowMsg.text}
        </p>
      )}
      {parts.length === 0 ? (
        !canEdit && <p className="ts-hint">No parts tracked yet.</p>
      ) : (
        <ul className="eq-parts-list">
          {parts.map((p) => {
            const status = partStatus(p);
            const svc = serviceStatus(p);
            const anchor = p.last_replaced_at || p.installed_at;
            return (
              <li key={p.id} className="eq-part-item">
                <div className="eq-part-main">
                  <div className="eq-part-name">{p.label || p.kind}</div>
                  <div className="eq-part-sub">
                    {p.replace_every_days
                      ? `replace every ${p.replace_every_days}d`
                      : 'no replace cadence'}
                    {anchor &&
                      ` · ${p.last_replaced_at ? 'replaced' : 'put in'} ${calendarDayAgo(anchor)}`}
                  </div>
                  {svc && (
                    <div
                      className="eq-part-sub"
                      style={{
                        color:
                          svc.kind === 'critical'
                            ? 'var(--alert)'
                            : svc.kind === 'low'
                              ? '#ffc166'
                              : 'var(--text-dim)',
                      }}
                    >
                      🔧 {p.service_label || 'Service'} · every {p.service_every_days}d ·{' '}
                      {svc.label}
                    </div>
                  )}
                </div>
                <span className={`eq-status eq-status-${status.kind}`}>{status.label}</span>
                <div className="eq-part-actions">
                  {canEdit && (
                    <button
                      type="button"
                      className="ts-chip"
                      onClick={() => onReplaced(p.id)}
                      disabled={busy === `replaced:${p.id}`}
                    >
                      {busy === `replaced:${p.id}` ? '…' : 'Replaced today'}
                    </button>
                  )}
                  {canEdit && svc && (
                    <button
                      type="button"
                      className="ts-chip"
                      onClick={() => onServiced(p.id)}
                      disabled={busy === `serviced:${p.id}`}
                    >
                      {busy === `serviced:${p.id}` ? '…' : 'Serviced today'}
                    </button>
                  )}
                  {(() => {
                    const url = resolveReorderUrl({
                      reorderUrl: p.reorder_url,
                      partKind: p.kind,
                      brand: equipment.brand,
                      library: config?.reorder,
                      preferredRetailer: user?.preferred_retailer,
                    });
                    return url ? (
                      <a href={url} target="_blank" rel="noopener noreferrer" className="ts-chip">
                        Reorder →
                      </a>
                    ) : null;
                  })()}
                  {canEdit && (
                    <>
                      <button
                        type="button"
                        className="ts-chip"
                        onClick={() =>
                          setEditing({
                            id: p.id,
                            label: p.label || '',
                            replace_every_days:
                              p.replace_every_days != null ? String(p.replace_every_days) : '',
                            reorder_url: p.reorder_url || '',
                            installed_at: p.installed_at ? String(p.installed_at).slice(0, 10) : '',
                          })
                        }
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="ts-chip ts-chip-danger"
                        onClick={() => onRemove(p.id)}
                        disabled={busy === `del-part:${p.id}`}
                      >
                        {busy === `del-part:${p.id}` ? '…' : 'Remove'}
                      </button>
                    </>
                  )}
                </div>
                {rowMsg?.id === p.id && (
                  <p role="alert" className="eq-field-error" style={{ gridColumn: '1 / -1' }}>
                    {rowMsg.text}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {/* Open with the card, no "+ Add part" first (owner, 2026-10-23: three
          taps at most, and a cartridge took four). */}
      {canEdit && (
        <AddPartInline
          tank={tank}
          equipmentId={equipment.id}
          catalogParts={catalogParts}
          suggested={unTrackedSuggestions(catalogParts, parts)}
          brand={equipment.brand}
          onAdded={async (row) => {
            if (row?.id) onAdded?.(row);
            try {
              await onReload();
            } catch {
              // Saved; it shows from justAdded until the next reload.
            }
          }}
        />
      )}
      {editing && (
        <EditPartDialog
          tank={tank}
          part={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await onReload();
            setEditing(null);
          }}
          setErr={setErr}
        />
      )}
    </div>
  );
}

function AddPartInline({ tank, equipmentId, catalogParts = [], suggested = [], brand, onAdded }) {
  const [label, setLabel] = useState('');
  const [days, setDays] = useState('365');
  const [daysTouched, setDaysTouched] = useState(false);
  const [url, setUrl] = useState('');
  const [urlErr, setUrlErr] = useState('');
  // Said beside Add part: the section's error line sits above the list, out
  // of sight on a phone.
  const [formErr, setFormErr] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const picked = catalogPartByName(catalogParts, label);

  const setName = (value) => {
    setLabel(value);
    setFormErr('');
    const match = catalogPartByName(catalogParts, value);
    // A service-only part (a skimmer cup to empty) has no replace interval:
    // 365 made one up and it read "Past due" a year on.
    if (!daysTouched) setDays(match ? String(match.replace_every_days || '') : '365');
  };
  const reset = () => {
    setLabel('');
    setDays('365');
    setDaysTouched(false);
    setUrl('');
    setUrlErr('');
  };

  const submit = async () => {
    if (inFlight.current) return;
    if (!label.trim()) {
      setFormErr('Give the part a name.');
      return;
    }
    const link = normalizeReorderLink(url);
    if (link.error || link.homepage) {
      setUrlErr(link.error ? PART_LINK_ERROR : PART_HOMEPAGE_HINT);
      return;
    }
    setUrlErr('');
    setFormErr('');
    inFlight.current = true;
    setBusy(true);
    const body = newPartBody({ label, days, link: link.url, catalogParts, brand, today: TODAY() });
    // One Add part keeps its id until it is answered, across a closed card
    // or a page left and come back to: the server returns the part already
    // saved for a repeat id, so a retry after a lost reply makes no twin.
    const scope = `part:${equipmentId}`;
    const ref = claimRequest(scope, body.label.toLowerCase());
    let row;
    try {
      row = await api(`/api/tanks/${tank.id}/equipment/${equipmentId}/parts`, {
        method: 'POST',
        body: { ...body, client_ref: ref },
      });
    } catch (e) {
      if (e.payload?.code === 'bad_reorder_link') setUrlErr(e.message);
      else setFormErr(unsureSaveWords(e) || e.message || 'Could not add the part.');
      inFlight.current = false;
      setBusy(false);
      return;
    }
    settleRequest(scope);
    // The first try was saved; bring it to what the form says now.
    const change = row?.duplicate ? retryChanges(row, body) : null;
    if (change) {
      row = await api(`/api/tanks/${tank.id}/equipment/parts/${row.id}`, {
        method: 'PATCH',
        body: change,
      }).catch(() => row);
    }
    reset();
    inFlight.current = false;
    setBusy(false);
    await onAdded(row);
  };

  return (
    <div className="eq-add-part">
      <div className="eq-parts-title">Add a part</div>
      {suggested.length > 0 && (
        <div className="eq-chip-row">
          {suggested.map((s) => {
            const on = picked === s;
            return (
              <button
                key={`${s.kind}-${s.label}`}
                type="button"
                className={`eq-chip eq-chip-tall${on ? ' eq-chip-on' : ''}`}
                aria-pressed={on}
                onClick={() => (on ? reset() : setName(s.label))}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      )}
      <input
        type="text"
        placeholder={suggested.length ? 'Or type a part name' : 'Part name (e.g. UV bulb)'}
        aria-label="Part name"
        value={label}
        maxLength={120}
        onChange={(e) => setName(e.target.value)}
        className="eq-text-input"
      />
      {label.trim() && (
        <>
          <div className="eq-part-cadence">
            <span className="ts-hint">replace every</span>
            <input
              type="number"
              min="1"
              inputMode="numeric"
              aria-label="Replace every, in days"
              value={days}
              onChange={(e) => {
                setDays(e.target.value);
                setDaysTouched(true);
              }}
              className="eq-part-days"
            />
            <span className="ts-hint">days, from today</span>
          </div>
          <input
            type="url"
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            placeholder="Link to reorder (optional)"
            aria-label="Product link to reorder"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setUrlErr('');
            }}
            className="eq-text-input"
          />
          {urlErr && (
            <p role="alert" className="eq-field-error">
              {urlErr}
            </p>
          )}
          <div className="eq-submit-row">
            <button type="button" className="ts-save" onClick={submit} disabled={busy}>
              {busy ? '…' : 'Add part'}
            </button>
          </div>
        </>
      )}
      {formErr && (
        <p role="alert" className="eq-field-error">
          {formErr}
        </p>
      )}
    </div>
  );
}

function EditPartDialog({ tank, part, onClose, onSaved, setErr }) {
  const [state, setState] = useState(part);
  const [busy, setBusy] = useState(false);
  // Errors show inside the sheet: the section's error line is behind it.
  const [sheetErr, setSheetErr] = useState('');

  const save = async () => {
    // Only a link the keeper changed is checked and sent: an older part
    // saved with "my LFS" must not block a change to "replace every"
    // (review, 2026-10-08; SuppliesView does the same).
    const linkChanged = (state.reorder_url || '') !== (part.reorder_url || '');
    const link = linkChanged ? normalizeReorderLink(state.reorder_url) : null;
    if (link && (link.error || link.homepage)) {
      setSheetErr(link.error ? PART_LINK_ERROR : PART_HOMEPAGE_HINT);
      return;
    }
    setSheetErr('');
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/equipment/parts/${state.id}`, {
        method: 'PATCH',
        body: {
          label: state.label,
          replace_every_days: state.replace_every_days
            ? Math.max(1, Number(state.replace_every_days) | 0)
            : null,
          ...(link ? { reorder_url: link.url } : {}),
          installed_at: state.installed_at || null,
        },
      });
      await onSaved();
    } catch (e) {
      setSheetErr(e.message || 'Could not save part.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ts-overlay" onClick={onClose}>
      <div
        className="ts-sheet ls-edit-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="ts-header">
          <h2 className="font-heading" style={{ fontSize: 20, fontWeight: 600 }}>
            Edit part
          </h2>
          <button type="button" aria-label="Close" className="ts-close" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="ts-row-body">
          <label className="ts-field" style={{ flex: 1 }}>
            <span>Name</span>
            <input
              type="text"
              value={state.label}
              maxLength={120}
              onChange={(e) => setState({ ...state, label: e.target.value })}
            />
          </label>
        </div>
        <div className="ts-row-body">
          <label className="ts-field" style={{ minWidth: 140 }}>
            <span>Installed</span>
            <input
              type="date"
              value={state.installed_at}
              onChange={(e) => setState({ ...state, installed_at: e.target.value })}
            />
          </label>
          <label className="ts-field" style={{ width: 140 }}>
            <span>Replace every (days)</span>
            <input
              type="number"
              min="1"
              value={state.replace_every_days}
              onChange={(e) => setState({ ...state, replace_every_days: e.target.value })}
            />
          </label>
        </div>
        <div className="ts-row-body">
          <label className="ts-field" style={{ flex: 1 }}>
            <span>Product link to reorder</span>
            <input
              type="url"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              value={state.reorder_url}
              onChange={(e) => {
                setState({ ...state, reorder_url: e.target.value });
                setSheetErr('');
              }}
              placeholder="bulkreefsupply.com/..."
            />
          </label>
          <button type="button" className="ts-save" onClick={save} disabled={busy}>
            {busy ? '…' : 'Save'}
          </button>
        </div>
        {sheetErr && (
          <p role="alert" className="eq-field-error" style={{ padding: '0 16px 12px' }}>
            {sheetErr}
          </p>
        )}
      </div>
    </div>
  );
}

function EditEquipmentDialog({ tank, catalog, editing, onClose, onSaved, setErr }) {
  const [state, setState] = useState(editing);
  const [busy, setBusy] = useState(false);
  const meta = kindMeta(catalog, state.kind);
  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/equipment/${state.id}`, {
        method: 'PATCH',
        body: {
          brand: state.brand,
          model: state.model,
          installed_at: state.installed_at || null,
          notes: state.notes,
        },
      });
      await onSaved();
    } catch (e) {
      setErr(e.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ts-overlay" onClick={onClose}>
      <div
        className="ts-sheet ls-edit-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="ts-header">
          <h2 className="font-heading" style={{ fontSize: 20, fontWeight: 600 }}>
            Edit {meta.label}
          </h2>
          <button type="button" aria-label="Close" className="ts-close" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="ts-row-body">
          <label className="ts-field" style={{ minWidth: 160, flex: '1 1 160px' }}>
            <span>Brand</span>
            <input
              type="text"
              value={state.brand}
              maxLength={80}
              onChange={(e) => setState({ ...state, brand: e.target.value })}
            />
          </label>
          <label className="ts-field" style={{ minWidth: 160, flex: '1 1 160px' }}>
            <span>Model</span>
            <input
              type="text"
              value={state.model}
              maxLength={120}
              onChange={(e) => setState({ ...state, model: e.target.value })}
            />
          </label>
        </div>
        <div className="ts-row-body">
          <label className="ts-field" style={{ minWidth: 140 }}>
            <span>Installed</span>
            <input
              type="date"
              value={state.installed_at}
              onChange={(e) => setState({ ...state, installed_at: e.target.value })}
            />
          </label>
          <label className="ts-field" style={{ flex: 1 }}>
            <span>Notes</span>
            <input
              type="text"
              value={state.notes}
              maxLength={500}
              onChange={(e) => setState({ ...state, notes: e.target.value })}
            />
          </label>
          <button type="button" className="ts-save" onClick={save} disabled={busy}>
            {busy ? '…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
